<script>
	import { localizeHref } from '@/paraglide/runtime';
</script>

<svelte:head>
	<title>Términos y Condiciones | Zarpar</title>
	<meta
		name="description"
		content="Términos y condiciones de uso de Zarpar. Conoce las condiciones legales para el alquiler de barcos entre particulares y el uso de nuestra plataforma náutica."
	/>
</svelte:head>

<div class="mx-auto max-w-3xl px-2 py-14 md:px-0">
	<div class="relative mx-auto max-w-150 pb-24 text-center">
		<h1 class="text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
			Términos y Condiciones
		</h1>
		<p class="mt-4 text-base leading-7 text-slate-600">Ultima actualización 01/09/2025</p>
	</div>
	<div
		class="prose prose-sm prose-slate prose-a:font-semibold prose-a:text-sky-500 hover:prose-a:text-sky-600 mx-auto max-w-160"
	>
		<h2 class="mb-4 text-lg font-semibold">Condiciones Generales de Uso</h2>
		<p>
			Se autoriza la contratación y/o uso de los servicios de ZARPAR disponibles en el Sitio Web
			<a href="https://zarpar.eu" class="font-semibold text-sky-500 hover:text-sky-600"
				>https://zarpar.eu</a
			> de conformidad con las presentes Condiciones Generales de Uso, Aviso Legal y Política de Privacidad.
		</p>

		<ol class="list-inside list-decimal space-y-2">
			<li class="mt-4 text-lg font-semibold">Objeto</li>
			<p>
				El objeto de estas Condiciones Generales de Uso es regular el acceso a los contenidos, así
				como el uso y contratación de los servicios prestados a través del Sitio Web.
			</p>
			<p>
				El acceso y uso de la página web es autorizado conforme a las presentes Condiciones
				Generales de Uso por <strong>PLATAFORMA NÁUTICA DIGITAL S.L.</strong> (en adelante, "<strong
					>ZARPAR</strong
				>").
			</p>
			<p>
				ZARPAR ha desarrollado una Plataforma a la que se puede acceder desde el sitio web
				<a href="https://www.zarpar.eu" class="font-semibold text-sky-500 hover:text-sky-600"
					>zarpar.eu</a
				>, que permite el acceso a los siguientes contenidos:
			</p>
			<ul class="mt-2 ml-4 list-disc space-y-2">
				<li>
					ZARPAR Experiencias: una plataforma de trayectos compartidos en barco diseñada para poner
					en contacto a los Armadores que tienen planeada una salida en su barco con pasajeros que
					quieran unirse a esa experiencia, de forma que puedan compartir la ruta, así como los
					costes asociados a la misma.
				</li>
				<li>
					ZARPAR Alquiler: Servicio de gestión del arrendamiento náutico de embarcaciones ofrecido a
					armadores y a pasajeros.
				</li>
				<li>ZARPAR Servicios: servicio de inspección y mantenimiento para embarcaciones.</li>
			</ul>

			<li class="mt-4 text-lg font-semibold">
				Carácter vinculante de las Condiciones Generales de Uso
			</li>
			<p>
				Las presentes Condiciones Generales de Uso de ZARPAR (en adelante, las "Condiciones
				Generales de Uso" o las "CGU") tienen carácter vinculante y regulan las condiciones de uso,
				acceso y contratación a los contenidos y/o los servicios ofrecidos por ZARPAR disponibles en
				la Plataforma.
			</p>
			<p>
				El acceso a los contenidos y/o servicios de ZARPAR atribuye la condición de usuario a quien
				los utiliza (en adelante, "Usuario") e implica la aceptación íntegra y completa de las
				presentes Condiciones Generales de Uso, así como del Aviso Legal y la Política de Privacidad
				que rigen el uso de la página web de ZARPAR.
			</p>
			<p>
				La aceptación de estas Condiciones Generales de Uso íntegra y sin reservas resulta
				indispensable para el uso y/o contratación de los contenidos y/o servicios de ZARPAR. El
				Usuario manifiesta haber leído, entendido y aceptado íntegramente las presentes Condiciones
				Generales de Uso. En el caso de que no esté de acuerdo con estos Condiciones Generales de
				Uso, por favor, no acceda ni use los servicios de ZARPAR.
			</p>
			<p>
				ZARPAR se reserva el derecho a actualizar las presentes Condiciones Generales de Uso, en
				cuyo caso ZARPAR publicará en este mismo sitio la versión actualizada de los Condiciones
				Generales de Uso, que pasará a ser vinculante desde su publicación. El Usuario debe revisar
				periódicamente estas CGU y en el supuesto de que no acepte una modificación de los mismos no
				podrá seguir utilizando los servicios de ZARPAR.
			</p>

			<li class="mt-4 text-lg font-semibold">Definiciones</li>
			<p>En este documento, los siguientes términos tienen el significado:</p>
			<ul class="mt-2 ml-4 list-disc">
				<li>
					<strong>"Anuncio"</strong> significa un anuncio relacionado con una Experiencia publicada por
					un usuario Armador en la Plataforma;
				</li>
				<li><strong>"CGU"</strong> significa las presentes Condiciones Generales de Uso;</li>
				<li>
					<strong>"Cuenta"</strong> significa la cuenta que debe crearse para ser Usuario y poder acceder
					a determinados servicios ofrecidos en el Sitio Web;
				</li>
				<li>
					<strong>"Armador"</strong> significa el Usuario que utiliza el Sitio Web como propietario de
					una embarcación;
				</li>
				<li>
					<strong>"Confirmación de reserva"</strong> tiene el significado que se le atribuye en el Artículo
					5.2.1 a continuación;
				</li>
				<li>
					<strong>"Contenidos"</strong> significa los Anuncios y demás contenido del área de Experiencias
					de la Plataforma.
				</li>
				<li>
					<strong>"Experiencia"</strong> significa la ruta en barco objeto de un Anuncio de Experiencia
					publicado por un Armador en el Sitio Web, por el cual el Armador acepta compartir un Trayecto
					en su propia embarcación con Pasajeros, también Usuarios de la plataforma, a cambio de compartir
					los gastos asociados a la Experiencia;
				</li>
				<li>
					<strong>"Gastos compartidos"</strong> significa, para una Experiencia, el importe solicitado
					por el Armador y aceptado por el Pasajero en concepto de contribución a los gastos del viaje
					o Experiencia;
				</li>
				<li>
					<strong>"Gastos de Gestión"</strong> significa los gastos solicitados por ZARPAR al Armador
					en concepto de puesta a disposición de la Plataforma ZARPAR Experiencias o acceso a los contenidos
					de la misma;
				</li>
				<li>
					<strong>"Pasajero"</strong> significa (i) el Usuario que ha aceptado compartir un Trayecto con
					el Armador, o, si procede, la persona en cuyo nombre ha reservado una Plaza un Usuario o (ii)
					el Usuario que ha contratado el Servicio de Arrendamiento de embarcaciones con patrón con ZARPAR;
				</li>
				<li>
					<strong>"Plataforma"</strong> tiene el significado que se le atribuye en el Artículo 1 de estas
					CGU.
				</li>
				<li>
					<strong>"Plaza"</strong> significa la plaza reservada por un Pasajero en la embarcación del
					Armador en una Experiencia, o bien como arrendatario en una embarcación arrendada por ZARPAR.
				</li>
				<li>
					<strong>"Prerreserva"</strong> se refiere a la operación por la que el Pasajero solicita la
					reserva de una Experiencia con un Armador, o del arrendamiento de una embarcación con ZARPAR.
				</li>
				<li>
					<strong>"Reserva"</strong> se refiere a la operación por la que el Armador o ZARPAR confirman
					al Pasajero respectivamente la Experiencia o el arrendamiento náutico.
				</li>
				<li>
					<strong>"Servicios"</strong> significa todos los servicios ofrecidos por ZARPAR por medio de
					la Plataforma;
				</li>
				<li>
					<strong>"Sitio web"</strong> significa el sitio web al que se puede acceder desde la
					dirección
					<a href="https://zarpar.eu" class="font-semibold text-sky-500 hover:text-sky-600"
						>zarpar.eu</a
					>;
				</li>
				<li>
					<strong>"Usuario"</strong> significa cualquier persona que haya creado una Cuenta en el Sitio
					Web.
				</li>
				<li>
					<strong>"Proveedor de la Solución de Pago"</strong> se refiere a Stripe Payments Europe, Limited
					("SPEL"); Stripe Technology Europe, Limited ("Stripe PSP"); Adyen N.V.
				</li>
			</ul>

			<li class="mt-4 text-lg font-semibold">Registro o alta de usuario en el sitio web</li>
			<p>
				El acceso a los contenidos o la contratación de servicios de ZARPAR sólo está permitido para
				personas mayores de edad y con capacidad legal para contratar. No se autoriza la
				contratación de servicios de ZARPAR a personas menores de edad, o que carezcan de capacidad
				para contratar.
			</p>
			<p>
				En caso de servicios para empresa, el Usuario garantiza que la persona que lee y acepta los
				presentes Condiciones Generales de Uso ostenta la condición de representante legal y dispone
				de la capacidad legal suficiente para contratar en nombre de la empresa.
			</p>
			<p>
				Para poder acceder a los servicios de ZARPAR, el Usuario deberá seguir el proceso de
				Registro o Alta de Usuario a través de la web <a
					href="https://zarpar.eu"
					class="font-semibold text-sky-500 hover:text-sky-600">https://zarpar.eu/</a
				> previa aceptación de la Política de Privacidad y estas CGU. La aceptación expresa de las presentes
				Condiciones Generales de Uso, así como de la Política de privacidad será imprescindible para completar
				el registro.
			</p>
			<p>
				El Usuario acepta expresamente y se obliga a proporcionar la información precisa, veraz,
				actual y completa que resulte necesaria al registrarse en la web de ZARPAR y en cualquier
				otro momento cuando sea necesario durante el uso de la misma (en adelante, los "Datos de
				Registro"), así como a mantener actualizados dichos datos.
			</p>
			<p>
				ZARPAR se reserva el derecho a denegar el acceso y utilización de los Servicios en caso de
				detectar o tener motivos razonables suficientes para considerar que el Usuario ha
				proporcionado datos inexactos, falsos o fraudulentos.
			</p>
			<p>
				El Usuario asume la responsabilidad de custodiar sus credenciales de acceso a su perfil de
				usuario en la web de ZARPAR, y exonera a ZARPAR de cualquier problema derivado o relacionado
				con su cuenta que sea resultado de no haber protegido o no haber adoptado medidas
				razonablemente adecuadas para proteger su nombre de usuario y/o su contraseña. El Usuario
				deberá mantener la confidencialidad sobre su nombre de usuario y contraseña de acceso a su
				cuenta y no permitirá que ninguna persona utilice su nombre de usuario, contraseña o
				cualquier otra forma de autorización.
			</p>
			<p>
				El Usuario es el único responsable de toda la actividad realizada en su cuenta, lo que
				incluye cualquier uso indebido de su nombre de usuario y contraseña, así como cualquier daño
				causado a causa de ese uso indebido. En caso de que el Usuario sospeche de un uso
				fraudulento o no autorizado de sus credenciales de usuario de ZARPAR, deberá comunicarlo de
				inmediato a la siguiente dirección de correo electrónico: <a
					href="mailto:info@zarpar.eu"
					class="font-semibold text-sky-500 hover:text-sky-600">info@zarpar.eu</a
				>
			</p>
			<p>
				El Usuario debe tener en cuenta que si decide realizar su registro o alta de usuario a
				través de servicios de autenticación y verificación de la cuenta mediante Apple o Google,
				las propias Condiciones Generales de Uso de estas compañías también pueden resultar de
				aplicación.
			</p>
			<p>
				El Armador puede enviar a ZARPAR, o subir a su área de usuario en ZARPAR fotografías de sus
				embarcaciones; el mero envío o subida de dichas fotografías, el Usuario autoriza a ZARPAR a
				publicar las mismas en su web y redes sociales.
			</p>
			<li class="mt-4 text-lg font-semibold">
				Acceso a los contenidos y/o utilización de los servicios
			</li>
			<p>
				ZARPAR ofrece contenidos y/o servicios diferenciados para Armadores y Pasajeros; para cada
				una de estas categorías de usuarios se establecen las siguientes normas:
			</p>

			<span class="mt-4 font-semibold">5.1 Armadores</span>
			<p>Como Armador, podrás acceder a los siguientes servicios de ZARPAR:</p>
			<ul class="mt-2 ml-4 list-disc space-y-2">
				<li>
					ZARPAR Experiencias: una plataforma donde podrás publicar anuncios para compartir
					trayectos en tu propio barco con costes compartidos.
				</li>
				<li>ZARPAR Alquiler: Encomendar a ZARPAR el arrendamiento náutico de tu barco.</li>

				<li>
					ZARPAR Servicios: servicio de inspección y mantenimiento integral para embarcaciones.
				</li>
			</ul>
			<p>
				Para el acceso y uso de cualquiera de estos servicios, el Armador debe ser el propietario de
				la embarcación o parte autorizada por el mismo, el cual con la aceptación de las presentes
				CGU admite estar autorizado legalmente por éste.
			</p>

			<span class="mt-4 font-semibold">5.1.1 ZARPAR Experiencias</span>
			<p>
				ZARPAR Experiencias es una parte de la Plataforma de ZARPAR, que permite el contacto entre
				Armadores y Pasajeros. ZARPAR actúa como mero intermediario entre estos, únicamente a los
				efectos de prestar el servicio de puesta en contacto entre usuarios y gestión de reservas y
				pagos.
			</p>
			<p>
				En cualquier caso, ZARPAR no forma parte de la relación contractual entre el Armador y los
				Pasajeros, ni ostenta ningún tipo de derecho sobre las embarcaciones publicadas. La
				formalización de una reserva y las transacciones dinerarias a través de la plataforma de
				ZARPAR no implica ni presupone la existencia de una relación contractual entre ZARPAR y los
				Usuarios.
			</p>
			<p>
				Cualquier modificación de las condiciones de la Reserva o la anulación de la misma deberá
				ser acordada entre el Armador y los Pasajeros.
			</p>
			<p>
				Para usar el servicio de ZARPAR Experiencias, el Armador debe cumplir todas y cada una de
				las condiciones indicadas a continuación:
			</p>
			<ul class="mt-2 ml-4 list-disc space-y-2">
				<li>
					poseer la licencia o títulos de navegación adecuados para navegar con la embarcación en
					vigor;
				</li>
				<li>
					publicar solamente Anuncios de experiencias o rutas compartidas en embarcaciones de su
					propiedad, que se encuentren en correcto estado de mantenimiento, con los posibles
					controles técnicos al día, y que dispongan de un sistema de seguridad apropiado para su
					categoría de navegación;
				</li>
				<li>ser el patrón de la embarcación del Anuncio de experiencia;</li>
				<li>
					que la embarcación cuente con un seguro de responsabilidad civil en vigor que cubra a
					todos los pasajeros;
				</li>
				<li>no tener ninguna contraindicación ni incapacidad médica para navegar;</li>
				<li>no publicar otro anuncio para la misma Experiencia en la Plataforma;</li>
				<li>
					no ofrecer más Plazas en el Anuncio de las comprendidas en la capacidad máxima de tu
					embarcación;
				</li>
				<li>todas las Plazas ofertadas cuentan con chaleco salvavidas;</li>
				<li>
					la embarcación se encuentra en buen estado y que cumple con todas las normativas legales,
					y que la embarcación dispone del Certificado Navegabilidad y la ITB actualizada;
				</li>
				<li>actuar como un consumidor y no como profesional;</li>
				<li>actuar sin ánimo de lucro.</li>
			</ul>
			<p>
				El Armador reconoce que es el único responsable del contenido del Anuncio/s de Experiencia
				que publique en la Plataforma, y garantiza la veracidad y precisión de toda la información
				incluida en el mismo. Asimismo se compromete a realizar el trayecto en barco objeto de la
				Experiencia en las condiciones del Anuncio, salvo circunstancias de fuerza mayor tales como
				climatológicas, o acuerdo con los Pasajeros.
			</p>
			<p>
				El Anuncio de Experiencia será publicado en la Plataforma y por lo tanto podrá ser visto por
				todos los Usuarios de la Plataforma. ZARPAR se reserva el derecho, a su exclusivo criterio,
				a no publicar o a borrar, en cualquier momento, cualquier Anuncio de Experiencia que no
				cumpla con las presentes CGU o que resulte potencialmente dañino para su imagen, así como a
				suspender la cuenta del Usuario que haya publicado dicho Anuncio. A modo de ejemplo, y sin
				carácter exhaustivo, serán causa de borrado del Anuncio y suspensión inmediata de la cuenta
				del Armador, las siguientes: impagos, comportamiento peligroso, o uso profesional
				encubierto.
			</p>
			<p>
				Se informa al Armador de que en caso de presentarse como consumidor utilizando la Plataforma
				cuando de hecho actúe como profesional, estará expuesto a las sanciones previstas por la ley
				aplicable. Queda expresamente prohibido el arrendamiento náutico por el Armador a terceros
				haciendo uso de la Plataforma.
			</p>
			<span class="mt-4 font-semibold">5.1.2 ZARPAR Alquiler</span>
			<p>
				El servicio de Zarpar alquiler se basa en el Real Decreto 186/2023, de 21 de marzo, por el
				que se aprueba el Reglamento de Ordenación de la Navegación Marítima (RONM), incluyó entre
				sus disposiciones un nuevo régimen del despacho de buques, cuya entrada en vigor fue el 15
				de agosto de 2025.
			</p>
			<p>
				Este servicio todavia no esta disponible para el publico, ya que estamos terminando de
				desarollar las gestiones necesarias con las autoridades competentes para poder llevarlo a
				cabo. Estos terminos y condiciones se actualizaran cuando este servicio este disponible para
				el publico.
			</p>

			<span class="mt-4 font-semibold">5.1.3 ZARPAR Servicios</span>
			<p>
				ZARPAR Servicios son los diferentes paquetes de servicios ofrecidos por ZARPAR para la
				revisión y mantenimiento de embarcaciones, que consisten en:
			</p>
			<ul class="mt-2 ml-4 list-disc space-y-2">
				<li>
					<strong>Revisión Zarpar:</strong> una revisión exhaustiva de tu embarcación. Usamos el sistema
					Zarpar para evaluar el estado de tu barco y recomendar arreglos en base a importancia y urgencia.
				</li>
				<li>
					<strong>Mantenimiento Anual Zarpar:</strong> incluye la aplicación de patente al barco y la
					gestión de la revisión del motor una vez al año, a cambio de un pago único, siempre que la embarcación
					se encuentre en buen estado general, que será juzgado por ZARPAR.
				</li>
				<li>
					<strong>Capitán Zarpar:</strong> una suscripción que incluye la Revisión Zarpar y el Mantenimiento
					anual, y adicionalmente una visita mensual a la embarcación por personal de ZARPAR; así como
					el seguimiento de los arreglos y trabajos en la misma durante todo el año.
				</li>
			</ul>
			<p>
				Es responsabilidad del Armador aportar la documentación legítima de la embarcación así como
				facilitar la información exacta y verídica.
			</p>
			<p>
				ZARPAR realizará un juicio profesional sobre los datos facilitados por parte del Usuario,
				por lo que es responsabilidad de este último el adecuar la toma de datos y medidas a lo
				exigido técnicamente para este tipo de procesos. ZARPAR pondrá a disposición del Usuario
				hojas de toma de datos si fuese necesario para guiar al Usuario, no siendo responsable de
				cualquier error o desviación ocurrido en dicho proceso. ZARPAR podrá rechazar el servicio a
				su sola discreción cuando las circunstancias de la embarcación no sean las adecuadas.
			</p>
			<p>
				Los precios pueden variar en función del estado de la embarcación una vez sea examinada por
				los técnicos correspondientes. En casos en que se detecten circunstancias excepcionales en
				la embarcación, ZARPAR podrá enviar un presupuesto adicional al Armador, que tendrá en ese
				caso la opción de aceptarlo o rechazarlo, debiendo asumir en todo caso los costes incurridos
				hasta la fecha. ZARPAR se reserva el derecho de declinar servicios o Usuarios cuya
				embarcación se encuentre en estado inapropiado.
			</p>
			<p>
				La subcontratación de ciertas partes o la totalidad del proceso puede ser necesaria debido a
				las circunstancias o características concretas de la embarcación. En base a esto, ZARPAR
				notificará al Usuario los plazos estimados inicialmente o las actualizaciones del mismo en
				caso de cambio de circunstancias.
			</p>
			<p>
				ZARPAR se reserva el derecho de no aceptar nuevas solicitudes de inspección procedentes de
				Usuarios con facturas pendientes de pago a su cargo.
			</p>
			<span class="mt-4 font-semibold">5.2 Pasajeros</span>
			<p>Como pasajero, podrás acceder a los siguientes servicios de ZARPAR:</p>
			<ul class="mt-2 ml-4 list-disc space-y-2">
				<li>
					ZARPAR Experiencias: una plataforma donde podrás reservar plaza en trayectos compartidos
					en barcos particulares asumiendo la parte proporcional de los costes del trayecto, así
					como contactar con el Armador que publica el anuncio de dicho trayecto.
				</li>
				<li>
					ZARPAR Alquiler: Arrendar una embarcación gestionada por ZARPAR con o sin patrón
					profesional.
				</li>
			</ul>

			<span class="mt-4 font-semibold">5.2.1 ZARPAR Experiencias</span>
			<p>
				Aquellos pasajeros interesados en disfrutar de una experiencia en una embarcación ofertada
				por un Armador ZARPAR deberán seleccionar la opción escogida, momento en que se generará una
				Prerreserva. El Armador recibirá una notificación de la misma, pudiendo aceptar o rechazar
				la reserva.
			</p>
			<p>
				En caso de que el Armador acepte la Prerreserva, se generaría una Reserva de la experiencia:
				el Pasajero deberá abonar el coste de los Gastos Compartidos y los Gastos de Gestión de la
				misma, tras lo que recibirá una Confirmación de Reserva, y tendrá la posibilidad de
				comunicarse directamente con el Armador.
			</p>
			<p>
				La relación contractual entre el Armador y el Pasajero será ajena a ZARPAR y las condiciones
				de la misma se pactarán exclusivamente entre el Armador y el Pasajero. ZARPAR no asume
				ninguna garantía ni responsabilidad respecto de los servicios acordados entre el Armador y
				el Pasajero, que son ajenos a la esfera de actuación de ZARPAR.
			</p>
			<p>
				ZARPAR no asumirá, en ningún caso, ningún tipo de responsabilidad por fallos en la
				embarcación, accidentes, daños o cualquier circunstancia derivada de la navegación, que
				serán responsabilidad exclusiva del Armador.
			</p>
			<p>
				El acceso a la plataforma ZARPAR Experiencias se ofrece exclusivamente para facilitarle al
				Armador y al Pasajero la posibilidad de compartir rutas y gastos.
			</p>

			<span class="mt-4 font-semibold"
				>5.2.2 Servicio de arrendamiento de embarcaciones con patrón</span
			>
			<p>
				ZARPAR ofrece el servicio de arrendamiento de embarcaciones con o sin patrón, a través del
				que el Pasajero podrá reservar Plaza en una embarcación capitaneada por un patrón
				profesional.
			</p>

			<li class="mt-4 text-lg font-semibold">Normas de uso del sitio web</li>
			<p>
				El Usuario podrá hacer uso de los Servicios de ZARPAR únicamente para el fin que le es
				propio de buena fe, de acuerdo con la legislación vigente, la moral y buenas costumbres
				generalmente aceptadas, el orden público y los presentes Condiciones Generales de Uso.
				Asimismo, el Usuario deberá respetar en todo momento los derechos de propiedad intelectual e
				industrial titularidad de ZARPAR.
			</p>
			<p>
				Queda estrictamente prohibido el uso de la página web y sus contenidos así como de los
				servicios de ZARPAR, con fines o efectos que sean efectiva o potencialmente ilícitos,
				prohibidos, lesivos de los derechos e intereses de terceros, así como la realización de
				cualquier acción que dañe o pueda dañar, inutilizar, sobrecargar o deteriorar y/o causar
				daños o alteraciones de cualquier tipo no consentidas por ZARPAR en su página web, a sus
				contenidos, o a otros Usuarios.
			</p>
			<p>
				De acuerdo con lo anterior, ZARPAR se reserva el derecho a denegar el acceso y utilización
				de la Plataforma y resto de servicios, incluyendo la suspensión o la eliminación de su
				cuenta, siempre que el Usuario realice un uso contrario a los presentes Condiciones
				Generales de Uso. En última instancia, en caso de ser necesario, ZARPAR también se reserva
				el derecho a emprender acciones legales siempre que lo considere oportuno.
			</p>

			<li class="mt-4 text-lg font-semibold">Precio y forma de pago</li>
			<p>
				El acceso al Sitio Web así como el registro como Usuario de la misma son gratuitos. No
				obstante, algunos de los Servicios ofrecidos a través de la misma son de pago.
			</p>
			<p>
				ZARPAR informará al Usuario del precio del servicio siempre con carácter previo a la
				contratación del mismo. ZARPAR informará al Usuario del precio final, (precio + IVA o en su
				caso otros impuestos o tasas aplicables) expresado en euros (€) de forma previa a la
				contratación.
			</p>

			<span class="mt-4 font-semibold"
				>7.1 Condiciones de pago del ZARPAR Servicios y los servicios de arrendamiento náutico</span
			>
			<p>
				La modalidad de pago disponible actualmente para "Zarpar Servicios" y los servicios de
				arrendamiento náutico es mediante tarjeta bancaria de débito o de crédito. Si se trata de un
				Usuario recurrente a través de facturación mensual, el pago se hará mediante domiciliación
				bancaria en el número de cuenta proporcionado por el Usuario dentro de los 5 primeros días
				de cada mes.
			</p>
			<p>
				En caso de impago, ZARPAR se reserva el derecho a interrumpir de inmediato la prestación de
				servicios así como a suspender la cuenta de usuario del Usuario. ZARPAR podrá ejercitar
				cuantas acciones legales (extrajudiciales o/y judiciales) resulten oportunas para obtener el
				cobro efectivo de lo adeudado, sus correspondientes intereses y gastos.
			</p>

			<span class="mt-4 font-semibold">7.2 Condiciones financieras de ZARPAR Experiencias</span>
			<p>
				En el caso de las Experiencias, los Gastos compartidos serán determinados por el Armador
				bajo su única responsabilidad. Está estrictamente prohibido obtener ningún tipo de beneficio
				mediante el uso de la plataforma de ZARPAR Experiencias. El Usuario Armador se compromete a
				limitar la Cantidad de Gastos Compartidos solicitada a tus Pasajeros a los gastos reales de
				la Experiencia que vaya a compartir con los Pasajeros.
			</p>
			<p>
				Al publicar un Anuncio Experiencia, ZARPAR podrá sugerir al Armador un importe en concepto
				de Gastos Compartidos, teniendo en cuenta las características y costes de mantenimiento
				general de la embarcación, la ruta o el coste del combustible, por ejemplo. Esta cantidad se
				indica solamente a modo de guía y el Armador podrá aumentarla o reducirla teniendo en cuenta
				los gastos totales reales de la Experiencia.
			</p>

			<span class="mt-4 font-semibold"
				>7.3 Fee o Gastos de Gestión para ZARPAR Experiencias y método de pago</span
			>
			<p>
				ZARPAR cobrará al Armador que publique Anuncios de Experiencias un fee en concepto de Gastos
				de Gestión, a cambio del uso de la Plataforma. El importe de los Gastos de Gestión será
				informado al Armador en el momento de publicar su Anuncio.
			</p>
			<p>En el momento de Confirmación de la Reserva, ZARPAR cobrará los Gastos de Gestión.</p>
			<p>
				Al utilizar la Plataforma como Armador para publicar Anuncios de Experiencias, el Armador
				confirma que ha leído y aceptado los términos y condiciones de Stripe, nuestro proveedor de
				pagos que procesa las transferencias de los Costes compartidos a los Armadores.
			</p>
			<p>
				Ten en cuenta que las determinadas exclusiones de la Directiva (UE) 2015/2366 del Parlamento
				Europeo y del Consejo, de 25 de noviembre de 2015, sobre servicios de pago en el mercado
				presentes en los TyC de nuestro Proveedor de la solución de pagos no se aplican a los
				Usuarios de la Plataforma como no profesionales.
			</p>
			<p>Como Armador, das instrucciones al Proveedor de la Solución de Pagos para que realice:</p>
			<ul class="mt-2 ml-4 list-disc">
				<li>la transferencia de los Gastos de Gestión a ZARPAR</li>
				<li>
					la transferencia del importe final de los Costes Compartidos de la Experiencia, una vez
					deducidos los Gastos de Gestión, a tu cuenta bancaria o de Stripe en tu nombre y por tu
					cuenta.
				</li>
			</ul>
			<p>
				Tras la Confirmación de la Reserva y hasta la finalización de la Experiencia, la cantidad
				total pagada por el Pasajero (Gastos de Gestión y Costes compartidos) se mantiene en una
				cuenta de depósito en garantía gestionada por el Proveedor de la Solución de Pago.
			</p>
			<p>
				Tras la Experiencia, los Pasajeros tendrán un período de 24 horas para presentar una
				reclamación a ZARPAR sobre el Trayecto. En ausencia de reclamación de los Pasajeros durante
				este período, ZARPAR deberá considerar la Experiencia como confirmada.
			</p>
			<p>
				A partir del momento de esta confirmación el Armador podrá solicitar el pago de la cantidad
				total pagada por el Pasajero en el momento de confirmar la Reserva tras haberle restado los
				Gastos de Gestión, es decir, la Cantidad de costes compartidos pagada por el Pasajero.
			</p>
			<p>
				La Solución de Pago es un servicio de pago proporcionado por el Proveedor de la Solución de
				Pago STRIPE. ZARPAR no presta servicios de procesamiento de pagos para los Usuarios, y no
				entra en posesión de los fondos o importes destinados al Armador.
			</p>

			<li class="mt-4 text-lg font-semibold">Derecho de desistimiento</li>

			<span class="mt-4 font-semibold">8.1 ZARPAR Servicios</span>
			<p>
				El Armador tendrá derecho a desistir del Contrato durante un periodo de catorce días
				naturales sin indicar motivo alguno, siempre y cuando ZARPAR no haya iniciado durante ese
				periodo los trabajos contratados, en cuyo caso será de aplicación lo previsto en el Artículo
				103.a) de la Ley General para la Defensa de Consumidores y Usuarios:
			</p>
			<p>
				La prestación de servicios, una vez que el servicio haya sido completamente ejecutado, y si
				el contrato impone al consumidor o usuario una obligación de pago, cuando la ejecución haya
				comenzado, con previo consentimiento expreso del consumidor o usuario y con el conocimiento
				por su parte de que, una vez que el empresario haya ejecutado íntegramente el contrato,
				habrá perdido su derecho de desistimiento.
			</p>

			<span class="mt-4 font-semibold">8.2 ZARPAR Experiencias</span>
			<p>
				De conformidad con lo establecido en el Artículo 103.a) de la Ley General para la Defensa de
				Consumidores y Usuarios, los Usuarios no tendrán derecho a desistimiento de una Experiencia
				a partir del momento de la Confirmación de reserva, toda vez que ésta supone la ejecución en
				su totalidad el Contrato entre el Usuario y ZARPAR, consistente en la puesta a disposición
				de la Plataforma para la exhibición de Anuncios.
			</p>

			<span class="mt-4 font-semibold">8.3 Arrendamiento náutico</span>
			<p>
				De conformidad con lo establecido en el Artículo 103.a) de la Ley General para la Defensa de
				Consumidores y Usuarios, los Usuarios no tendrán derecho a desistimiento del arrendamiento
				náutico a partir del momento de la Confirmación de reserva, toda vez que ésta supone la
				ejecución en su totalidad el Contrato entre el Usuario y ZARPAR, consistente en gestionar
				dicho arrendamiento.
			</p>

			<li class="mt-4 text-lg font-semibold">Protección de datos y confidencialidad</li>
			<p>
				Para mayor información sobre nuestra política de protección de datos, puede consultar
				nuestra
				<a
					href={localizeHref('/politica-de-privacidad')}
					class="font-semibold text-sky-500 hover:text-sky-600">Política de Privacidad</a
				>.
			</p>

			<li class="mt-4 text-lg font-semibold">Modificación</li>
			<p>
				ZARPAR se reserva el derecho a modificar los presentes Condiciones Generales de Uso para
				adaptarlos a cualquier cambio o nueva normativa, por razones técnicas, por cambios en los
				Servicios ofrecidos por ZARPAR o por decisiones estratégicas de la empresa. Las
				modificaciones de estos Condiciones Generales de Uso serán publicadas en la misma forma en
				que aparecen estos o a través de cualquier tipo de comunicación dirigida al Usuario.
			</p>
			<p>
				En todo caso, el acceso y utilización de los Servicios de ZARPAR tras la entrada en vigor de
				las modificaciones o cambios supone la aceptación de los nuevos Condiciones Generales de
				Uso. Por el contrario, si el Usuario no está de acuerdo con los cambios en los presentes
				Condiciones Generales de Uso, deberá dejar de utilizar con carácter inmediato ZARPAR.
			</p>

			<li class="mt-4 text-lg font-semibold">Contacto</li>
			<p>
				El Usuario podrá ponerse en contacto con ZARPAR para cualquier cuestión relativa a los
				presentes Condiciones Generales de Uso en la siguiente dirección de correo electrónico:
				<a href="mailto:info@zarpar.eu" class="font-semibold text-sky-500 hover:text-sky-600"
					>info@zarpar.eu</a
				>
			</p>
		</ol>
	</div>
</div>
